import { DateTime } from 'luxon';
import { parseSheet } from './workbook.parser';
import type {
  ScheduleEventGroupPayload,
  ScheduleEventPayload,
  SheetDefect,
  SheetGrid,
} from './workbook.types';
import { ISSUE_REASONS, TAB_EXCLUSION_REASONS, TAB_STATUSES } from './upload.types';
import type {
  ImportResult,
  ImportScheduleResponse,
  SheetIssue,
  TabAnalysis,
} from './upload.types';
import { collectIssues, countExcludedTabs, countSkippedRows } from './upload.utils';

export type ImportPlan = {
  tabs: TabAnalysis[];
  groups: ScheduleEventGroupPayload[];
  events: ScheduleEventPayload[];
  startDate: string | null;
  endDate: string | null;
};

const toOrgDate = (isoInstant: string, orgTimezone: string) =>
  DateTime.fromISO(isoInstant, { zone: orgTimezone }).toFormat('yyyy-LL-dd');

// Mirrors the deletion predicate overrideEvents uses: an event belongs to the
// day it starts on, so both bounds are on startTime and the upper one is
// exclusive. Filtering on endTime instead would miss shifts that start on the
// last day and run past midnight.
export const toOverrideWindow = (
  startDate: string,
  endDate: string,
  orgTimezone: string,
): { startTime: string; startsBefore: string } => ({
  startTime: DateTime.fromISO(startDate, { zone: orgTimezone }).startOf('day').toUTC().toISO()!,
  startsBefore: DateTime.fromISO(endDate, { zone: orgTimezone })
    .startOf('day')
    .plus({ days: 1 })
    .toUTC()
    .toISO()!,
});

const excludedTab = (sheetName: string, defect: SheetDefect): TabAnalysis => ({
  sheetName,
  status: TAB_STATUSES.EXCLUDED,
  importedCount: 0,
  skippedCount: 0,
  issues: [],
  exclusionReason: defect,
});

export const buildImportPayload = ({
  plan,
  fileName,
  sheets,
}: {
  plan: ImportPlan;
  fileName: string;
  sheets: string[];
}) => ({
  scheduleEventGroups: plan.groups,
  scheduleEvents: plan.events,
  ...(plan.startDate && plan.endDate
    ? { overrideEvents: { startDate: plan.startDate, endDate: plan.endDate } }
    : {}),
  fileName,
  sheets,
});

export const buildImportResult = ({
  response,
  plan,
  fileName,
  completedAt,
}: {
  response: ImportScheduleResponse;
  plan: ImportPlan;
  fileName: string;
  completedAt: number;
}): ImportResult => {
  const excludedTabs = countExcludedTabs(plan.tabs);

  return {
    id: `${fileName}-${completedAt}`,
    fileName,
    completedAt,
    importedCount: response.scheduleEventsCreated,
    skippedCount: countSkippedRows(plan.tabs),
    eventsDeleted: response.eventsDeleted,
    tabsImported: plan.tabs.length - excludedTabs,
    tabsTotal: plan.tabs.length,
    tabsSkipped: excludedTabs,
    issues: collectIssues(plan.tabs),
  };
};

type SheetAnalysis = {
  tab: TabAnalysis;
  events: ScheduleEventPayload[];
  groups: ScheduleEventGroupPayload[];
  dateRange: { start: string; end: string } | null;
};

const analyseSheet = (
  grid: SheetGrid,
  orgTimezone: string,
  knownEmails: Set<string>,
): SheetAnalysis => {
  const parsed = parseSheet(grid, orgTimezone);
  const nothingToImport = { events: [], groups: [], dateRange: null };

  if (parsed.defect) {
    return { tab: excludedTab(grid.sheetName, parsed.defect), ...nothingToImport };
  }

  const issues: SheetIssue[] = parsed.rejectedCells.map(cell => ({
    sheetName: grid.sheetName,
    shiftTitle: cell.shiftTitle,
    date: cell.date,
    reason: ISSUE_REASONS.INVALID_ATTENDEE,
    reasonContext: { value: cell.value },
  }));

  const importable = parsed.events.filter(event => {
    const userEmail = event.attendees[0]!.userEmail;
    if (knownEmails.has(userEmail)) return true;

    issues.push({
      sheetName: grid.sheetName,
      shiftTitle: event.title,
      date: toOrgDate(event.startTime, orgTimezone),
      reason: ISSUE_REASONS.USER_NOT_FOUND,
      reasonContext: { value: userEmail },
    });
    return false;
  });

  if (importable.length === 0) {
    return {
      tab: {
        sheetName: grid.sheetName,
        status: TAB_STATUSES.EXCLUDED,
        importedCount: 0,
        skippedCount: issues.length,
        issues,
        exclusionReason: TAB_EXCLUSION_REASONS.NOTHING_TO_IMPORT,
      },
      ...nothingToImport,
    };
  }

  return {
    tab: {
      sheetName: grid.sheetName,
      status: issues.length > 0 ? TAB_STATUSES.PARTIAL : TAB_STATUSES.CLEAN,
      importedCount: importable.length,
      skippedCount: issues.length,
      issues,
    },
    events: importable,
    groups: parsed.groups,
    dateRange: parsed.dateRange,
  };
};

const hasDifferentHours = (a: ScheduleEventGroupPayload, b: ScheduleEventGroupPayload) =>
  a.isAllDay !== b.isAllDay ||
  a.startTime.hour !== b.startTime.hour ||
  a.startTime.minute !== b.startTime.minute ||
  a.endTime.hour !== b.endTime.hour ||
  a.endTime.minute !== b.endTime.minute;

// scheduleEventGroupName is the join key the API uses to bind events to groups,
// so two sheets cannot contribute different hours under one name. First writer
// wins; anything else would be dropped silently.
const collectGroups = (analyses: SheetAnalysis[]) => {
  const byName = new Map<string, ScheduleEventGroupPayload>();

  analyses.forEach(({ tab, groups }) => {
    groups.forEach(group => {
      const existing = byName.get(group.scheduleEventGroupName);

      if (!existing) {
        byName.set(group.scheduleEventGroupName, group);
      } else if (hasDifferentHours(existing, group)) {
        console.warn(
          `Shift import: "${group.scheduleEventGroupName}" appears in more than one sheet with different hours; keeping the first and ignoring the definition from "${tab.sheetName}"`,
        );
      }
    });
  });

  return [...byName.values()];
};

export const buildImportPlan = ({
  grids,
  selectedTabs,
  orgTimezone,
  knownEmails,
}: {
  grids: SheetGrid[];
  selectedTabs: string[];
  orgTimezone: string;
  knownEmails: Set<string>;
}): ImportPlan => {
  const selected = new Set(selectedTabs);
  const analyses = grids
    .filter(grid => selected.has(grid.sheetName))
    .map(grid => analyseSheet(grid, orgTimezone, knownEmails));

  // Only sheets that import something contribute, so a skipped sheet's dates are
  // never cleared — but a contributing sheet clears its whole span, including
  // days whose rows did not resolve.
  const coveredDates = analyses.flatMap(({ dateRange }) =>
    dateRange ? [dateRange.start, dateRange.end] : [],
  );

  return {
    tabs: analyses.map(({ tab }) => tab),
    groups: collectGroups(analyses),
    events: analyses.flatMap(({ events }) => events),
    startDate: coveredDates.reduce<string | null>(
      (min, date) => (min === null || date < min ? date : min),
      null,
    ),
    endDate: coveredDates.reduce<string | null>(
      (max, date) => (max === null || date > max ? date : max),
      null,
    ),
  };
};
